/**
 * Medicion de Google para el sitio publico de Tico Baterias.
 *
 * - La etiqueta y las conversiones se configuran en admin > Google / Medicion y se
 *   leen de /api/google/publico. Si esta apagado, no se carga NADA de Google.
 * - Los clics a WhatsApp (wa.me) y telefono (tel:) se detectan solos en todo el sitio,
 *   incluidos botones que se agreguen en el futuro. No hay que tocar cada boton.
 *   Opcional: data-origen="texto" en un enlace para nombrarlo en las estadisticas.
 * - La cotizacion del agente se registra desde ChatTicoBaterin.tsx.
 */

export type TipoEvento = 'whatsapp' | 'telefono' | 'cotizacion';

type ConfigGoogle = {
  tag_id: string | null;
  conversiones: Partial<Record<TipoEvento, string>>;
  eventos: Partial<Record<TipoEvento, string>>;
};

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const EVENTOS_POR_DEFECTO: Record<TipoEvento, string> = {
  whatsapp: 'clic_whatsapp',
  telefono: 'clic_telefono',
  cotizacion: 'cotizacion_agente',
};

const RE_WHATSAPP = /^https?:\/\/(wa\.me|api\.whatsapp\.com|(web\.)?whatsapp\.com)\//i;

let config: ConfigGoogle | null = null;
let iniciado = false;
const enviados = new Set<string>();

export async function iniciarMedicion(): Promise<void> {
  if (iniciado) return; // React StrictMode puede llamar dos veces
  iniciado = true;

  try {
    const r = await fetch('/api/google/publico');
    if (!r.ok) return;
    config = (await r.json()) as ConfigGoogle;
  } catch {
    return; // sin medicion, pero el sitio sigue funcionando normal
  }
  if (!config || !config.tag_id) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag.js necesita el objeto "arguments" tal cual, no un arreglo
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', config.tag_id);

  // Destinos de Google Ads de las conversiones (AW-123456789)
  const destinosAds = new Set(
    Object.values(config.conversiones || {})
      .filter((v): v is string => !!v)
      .map(v => v.split('/')[0])
  );
  destinosAds.forEach(aw => window.gtag && window.gtag('config', aw));

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(config.tag_id)}`;
  document.head.appendChild(script);

  escucharClics();
}

/**
 * Registra un evento en Analytics y, si esta configurada, la conversion de Google Ads.
 * unaVezPor: clave para no contar lo mismo dos veces en la misma visita.
 */
export function registrarEvento(
  tipo: TipoEvento,
  datos: Record<string, string | number> = {},
  unaVezPor?: string
): void {
  if (!config || !config.tag_id || !window.gtag) return;
  if (unaVezPor) {
    if (enviados.has(unaVezPor)) return;
    enviados.add(unaVezPor);
  }
  const nombre = (config.eventos && config.eventos[tipo]) || EVENTOS_POR_DEFECTO[tipo];
  window.gtag('event', nombre, { ...datos, pagina: window.location.pathname });

  const sendTo = config.conversiones && config.conversiones[tipo];
  if (sendTo) window.gtag('event', 'conversion', { send_to: sendTo });
}

function textoBoton(a: HTMLAnchorElement): string {
  const texto = a.dataset.origen || a.textContent || '';
  return texto.replace(/\s+/g, ' ').trim().slice(0, 60) || 'sin texto';
}

function escucharClics(): void {
  // Enlaces <a href="https://wa.me/..."> y <a href="tel:...">, en cualquier parte del sitio
  document.addEventListener(
    'click',
    e => {
      const destino = e.target as Element | null;
      if (!destino || typeof destino.closest !== 'function') return;
      const a = destino.closest('a[href]') as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (RE_WHATSAPP.test(href)) {
        registrarEvento('whatsapp', { boton: textoBoton(a) });
      } else if (href.toLowerCase().startsWith('tel:')) {
        registrarEvento('telefono', { boton: textoBoton(a) });
      }
    },
    true
  );

  // Botones que abren WhatsApp con window.open (por ejemplo, el del chat)
  const abrirOriginal = window.open.bind(window);
  window.open = function (url?: string | URL, target?: string, features?: string) {
    if (url && RE_WHATSAPP.test(String(url))) {
      registrarEvento('whatsapp', { boton: 'Chat TicoBaterin' });
    }
    return abrirOriginal(url, target, features);
  };
}
